import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { 
  X, Cpu, HardDrive, MemoryStick, Clock, 
  Camera, Code, Terminal, Square,
  Trash2, Download, Copy, Pause, Play,
  ChevronDown, Bug, Activity, MousePointer, Send
} from 'lucide-react';

const slideIn = keyframes`
  from { transform: translateX(100%); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
`;

const Panel = styled.div`
  position: fixed;
  top: 0;
  right: ${p => p.$rightOffset || '0'};
  width: 420px;
  height: 100vh;
  background: var(--bg);
  border-left: 1px solid var(--border);
  z-index: 1100;
  display: flex;
  flex-direction: column;
  box-shadow: -4px 0 24px rgba(0, 0, 0, 0.4);
  animation: ${slideIn} 0.25s ease-out;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid var(--border);
  background: var(--surface);
  flex-shrink: 0;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const HeaderTitle = styled.h3`
  font-size: 15px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
`;

const HeaderBadge = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: var(--primary);
  background: var(--surface-hover);
  padding: 2px 8px;
  border-radius: 4px;
`;

const IconBtn = styled.button`
  background: transparent;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  padding: 6px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;

  &:hover {
    background: var(--surface-hover);
    color: var(--text-primary);
  }
`;

const CloseBtn = styled(IconBtn)`
  &:hover {
    background: rgba(239, 68, 68, 0.15);
    color: #ef4444;
  }
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  padding: 12px 16px;
  border-bottom: 1px solid var(--border);
  flex-shrink: 0;
`;

const StatCard = styled.div`
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 8px;
  text-align: center;
`;

const StatIcon = styled.div`
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StatValue = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: var(--text-primary);
`;

const StatLabel = styled.div`
  font-size: 9px;
  font-weight: 600;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Section = styled.div`
  padding: 12px 16px;
  border-bottom: 1px solid var(--border);
  flex-shrink: 0;
`;

const SectionTitle = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: var(--primary);
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ProcessList = styled.div`
  color: var(--text-muted);
  font-size: 13px;
  padding: 8px 0;
`;

const DeviceSelector = styled.select`
  width: 100%;
  padding: 8px 12px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 6px;
  color: var(--text-primary);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  margin-bottom: 10px;

  &:focus {
    outline: none;
    border-color: var(--primary);
  }
`;

const ToolsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 6px;
`;

const ToolButton = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 10px 4px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 8px;
  color: var(--text-secondary);
  cursor: pointer;
  font-size: 9px;
  font-weight: 600;
  transition: all 0.15s;

  &:hover {
    background: var(--surface-hover);
    border-color: var(--primary);
    color: var(--primary);
  }

  svg { width: 18px; height: 18px; }
`;

const StopButton = styled(ToolButton)`
  background: rgba(239, 68, 68, 0.1);
  border-color: rgba(239, 68, 68, 0.3);
  color: #ef4444;
  &:hover { background: rgba(239, 68, 68, 0.2); border-color: #ef4444; }
`;

const TabBar = styled.div`
  display: flex;
  border-bottom: 1px solid var(--border);
  flex-shrink: 0;
`;

const Tab = styled.button`
  flex: 1;
  padding: 10px 8px;
  background: transparent;
  border: none;
  border-bottom: 2px solid ${p => p.$active ? 'var(--primary)' : 'transparent'};
  color: ${p => p.$active ? 'var(--primary)' : 'var(--text-muted)'};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;

  &:hover { color: var(--text-primary); background: var(--surface); }
`;

const TabBadge = styled.span`
  font-size: 10px;
  background: ${p => p.$hasItems ? 'rgba(239, 68, 68, 0.2)' : 'var(--surface)'};
  color: ${p => p.$hasItems ? '#ef4444' : 'var(--text-muted)'};
  padding: 1px 6px;
  border-radius: 10px;
  font-weight: 600;
`;

const LogsContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px;
  font-family: 'JetBrains Mono', 'Fira Code', 'Consolas', monospace;
  font-size: 12px;
  line-height: 1.6;
  background: var(--bg);
`;

const LogLine = styled.div`
  padding: 2px 8px;
  border-radius: 3px;
  color: ${p => {
    switch (p.$level) {
      case 'error': return '#ef4444';
      case 'warn': return '#f59e0b';
      case 'info': return 'var(--primary)';
      case 'success': return '#10b981';
      default: return 'var(--text-secondary)';
    }
  }};
  &:hover { background: var(--surface); }
`;

const LogTimestamp = styled.span`
  color: var(--text-muted);
  margin-right: 8px;
  font-size: 11px;
`;

const LogTag = styled.span`
  color: var(--primary);
  font-weight: 600;
  margin-right: 6px;
`;

const LogToolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 8px;
  border-top: 1px solid var(--border);
  flex-shrink: 0;
`;

const FilterBtn = styled.button`
  padding: 4px 10px;
  background: ${p => p.$active ? 'rgba(165, 145, 239, 0.15)' : 'transparent'};
  border: 1px solid ${p => p.$active ? 'var(--primary)' : 'var(--border)'};
  border-radius: 4px;
  color: ${p => p.$active ? 'var(--primary)' : 'var(--text-muted)'};
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  &:hover { border-color: var(--primary); color: var(--primary); }
`;

const UiDumpResult = styled.div`
  max-height: 200px; overflow-y: auto; font-size: 11px;
  background: var(--bg); border: 1px solid var(--border); border-radius: 6px;
  padding: 6px 8px; margin-top: 8px;
`;

const UiElement = styled.div`
  padding: 3px 6px; border-radius: 3px; cursor: pointer;
  font-family: 'JetBrains Mono', monospace; font-size: 10px;
  color: var(--text-secondary); display: flex; gap: 6px; align-items: center;
  &:hover { background: var(--surface-hover); color: var(--primary); }
`;

const ShellInput = styled.input`
  flex: 1; padding: 6px 10px; background: var(--bg); border: 1px solid var(--border);
  border-radius: 6px; color: var(--text-primary); font-size: 12px;
  font-family: 'JetBrains Mono', monospace; outline: none;
  &:focus { border-color: var(--primary); }
`;

const ScreenshotPreview = styled.img`
  max-width: 100%; max-height: 180px; border-radius: 6px; margin-top: 8px;
  border: 1px solid var(--border); cursor: crosshair;
`;

const DebugConsole = ({ isOpen, onClose, rightOffset }) => {
  const [activeTab, setActiveTab] = useState('logs');
  const [logs, setLogs] = useState([]);
  const [filter, setFilter] = useState('all');
  const [stats, setStats] = useState({ cpu: '—', ram: '—', memory: '—', uptime: '—' });
  const [selectedDevice, setSelectedDevice] = useState('');
  const [devices, setDevices] = useState([]);
  const [paused, setPaused] = useState(false);
  const logsEndRef = useRef(null);
  const [uiDumpElements, setUiDumpElements] = useState(null);
  const [screenshotSrc, setScreenshotSrc] = useState(null);
  const [shellCmd, setShellCmd] = useState('');
  const [toolLoading, setToolLoading] = useState(null);

  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);
  const pausedRef = useRef(false);

  useEffect(() => {
    if (!isOpen) return;

    const fetchStats = async () => {
      try {
        const res = await fetch('/api/system/stats');
        if (res.ok) setStats(await res.json());
      } catch (e) { /* defaults */ }
    };

    const fetchDevices = async () => {
      try {
        const res = await fetch('/api/devices');
        if (res.ok) {
          const data = await res.json();
          const list = Array.isArray(data) ? data : (Array.isArray(data.devices) ? data.devices : []);
          setDevices(list);
          if (list.length > 0 && !selectedDevice) {
            setSelectedDevice(list[0].device_id || list[0].id);
          }
        }
      } catch (e) { /* ignore */ }
    };

    fetchStats();
    fetchDevices();
    const interval = setInterval(fetchStats, 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // WebSocket connection to server logs
  useEffect(() => {
    if (!isOpen) return;

    let alive = true;

    const formatTs = (iso) => {
      try { return new Date(iso).toLocaleTimeString('fr-FR', { hour12: false }); }
      catch { return iso; }
    };

    function connect() {
      if (!alive) return;
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const host = window.location.hostname || 'localhost';
      const ws = new window.WebSocket(`${protocol}//${host}:3002/ws/server-logs`);
      wsRef.current = ws;

      ws.onopen = () => {
        const ts = new Date().toLocaleTimeString('fr-FR', { hour12: false });
        setLogs(prev => [...prev.slice(-499), { level: 'success', message: 'Connected to server log stream', tag: 'WS', ts, id: Date.now() + Math.random() }]);
      };

      ws.onmessage = (evt) => {
        if (pausedRef.current) return;
        try {
          const data = JSON.parse(evt.data);
          if (data.type === 'history' && Array.isArray(data.logs)) {
            setLogs(prev => {
              const existing = new Set(prev.map(l => l.id));
              const newLogs = data.logs
                .filter(l => !existing.has(l.id))
                .map(l => ({ ...l, ts: formatTs(l.ts) }));
              return [...prev, ...newLogs].slice(-500);
            });
          } else if (data.type === 'log') {
            setLogs(prev => [...prev.slice(-499), { ...data, ts: formatTs(data.ts) }]);
          }
        } catch { /* ignore parse errors */ }
      };

      ws.onclose = () => {
        if (alive) {
          reconnectTimer.current = setTimeout(connect, 3000);
        }
      };

      ws.onerror = () => { /* onclose will fire */ };
    }

    connect();

    return () => {
      alive = false;
      clearTimeout(reconnectTimer.current);
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (!paused && logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs, paused]);

  const addLog = (level, message, tag = 'SYSTEM') => {
    const ts = new Date().toLocaleTimeString('fr-FR', { hour12: false });
    setLogs(prev => [...prev.slice(-500), { level, message, tag, ts, id: Date.now() + Math.random() }]);
  };

  const postDebug = async (endpoint, body = {}) => {
    const res = await fetch(`/api/devices/debug/${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ device_id: selectedDevice, ...body })
    });
    return res.json();
  };

  const handleUiDump = async () => {
    if (!selectedDevice) { addLog('warn', 'Select a device first', 'DEBUG'); return; }
    setToolLoading('ui-dump');
    addLog('info', `UI Dump on ${selectedDevice}...`, 'DEBUG');
    try {
      const data = await postDebug('ui-dump');
      if (data.success) {
        setUiDumpElements(data.elements);
        addLog('success', `UI Dump: ${data.element_count} elements found`, 'DEBUG');
      } else {
        addLog('error', `UI Dump failed: ${data.error}`, 'DEBUG');
      }
    } catch (e) { addLog('error', `UI Dump error: ${e.message}`, 'DEBUG'); }
    setToolLoading(null);
  };

  const handleScreenshot = async () => {
    if (!selectedDevice) { addLog('warn', 'Select a device first', 'DEBUG'); return; }
    setToolLoading('screenshot');
    addLog('info', `Screenshot on ${selectedDevice}...`, 'DEBUG');
    try {
      const data = await postDebug('screenshot');
      if (data.success) {
        setScreenshotSrc(data.image);
        addLog('success', 'Screenshot captured', 'DEBUG');
      } else {
        addLog('error', `Screenshot failed: ${data.error}`, 'DEBUG');
      }
    } catch (e) { addLog('error', `Screenshot error: ${e.message}`, 'DEBUG'); }
    setToolLoading(null);
  };

  const handleTap = async (x, y) => {
    if (!selectedDevice) return;
    addLog('info', `Tap (${x}, ${y}) on ${selectedDevice}`, 'DEBUG');
    try {
      await postDebug('tap', { x, y });
      addLog('success', `Tapped (${x}, ${y})`, 'DEBUG');
    } catch (e) { addLog('error', `Tap error: ${e.message}`, 'DEBUG'); }
  };

  const handleShell = async () => {
    if (!selectedDevice || !shellCmd.trim()) return;
    setToolLoading('shell');
    addLog('info', `$ ${shellCmd}`, 'SHELL');
    try {
      const data = await postDebug('shell', { command: shellCmd });
      if (data.output) addLog(data.success ? 'log' : 'warn', data.output, 'SHELL');
      else addLog('success', '(no output)', 'SHELL');
    } catch (e) { addLog('error', `Shell error: ${e.message}`, 'SHELL'); }
    setShellCmd('');
    setToolLoading(null);
  };

  const handleScreenshotClick = (e) => {
    if (!screenshotSrc) return;
    const rect = e.target.getBoundingClientRect();
    const scaleX = e.target.naturalWidth / rect.width;
    const scaleY = e.target.naturalHeight / rect.height;
    const x = Math.round((e.clientX - rect.left) * scaleX);
    const y = Math.round((e.clientY - rect.top) * scaleY);
    handleTap(x, y);
  };

  const tabLogs = activeTab === 'errors' 
    ? logs.filter(l => l.level === 'error' || l.level === 'warn')
    : activeTab === 'status' 
      ? logs.filter(l => l.level === 'success')
      : logs;
  const filteredLogs = filter === 'all' ? tabLogs : tabLogs.filter(l => l.level === filter);
  const errorCount = logs.filter(l => l.level === 'error').length;
  const warnCount = logs.filter(l => l.level === 'warn').length;

  if (!isOpen) return null;

  return (
    <Panel $rightOffset={rightOffset}>
      <Header>
        <HeaderLeft>
          <Bug size={16} style={{ color: 'var(--primary)' }} />
          <HeaderTitle>Debug Console</HeaderTitle>
          <HeaderBadge>Admin</HeaderBadge>
        </HeaderLeft>
        <div style={{ display: 'flex', gap: 4 }}>
          <IconBtn title="Réduire"><ChevronDown size={14} /></IconBtn>
          <CloseBtn onClick={onClose} title="Fermer"><X size={14} /></CloseBtn>
        </div>
      </Header>

      <StatsRow>
        <StatCard>
          <StatIcon><Cpu size={16} color="#3b82f6" /></StatIcon>
          <StatValue>{stats.cpu}</StatValue>
          <StatLabel>CPU</StatLabel>
        </StatCard>
        <StatCard>
          <StatIcon><HardDrive size={16} color="#8b5cf6" /></StatIcon>
          <StatValue>{stats.ram}</StatValue>
          <StatLabel>RAM</StatLabel>
        </StatCard>
        <StatCard>
          <StatIcon><MemoryStick size={16} color="#f59e0b" /></StatIcon>
          <StatValue>{stats.memory}</StatValue>
          <StatLabel>Mem %</StatLabel>
        </StatCard>
        <StatCard>
          <StatIcon><Clock size={16} color="#10b981" /></StatIcon>
          <StatValue>{stats.uptime}</StatValue>
          <StatLabel>Uptime</StatLabel>
        </StatCard>
      </StatsRow>

      <Section>
        <SectionTitle><Activity size={12} /> BOT PROCESSES (0)</SectionTitle>
        <ProcessList>No bot running</ProcessList>
      </Section>

      <Section>
        <SectionTitle><Bug size={12} /> DEBUG TOOLS</SectionTitle>
        <DeviceSelector value={selectedDevice} onChange={e => { setSelectedDevice(e.target.value); setUiDumpElements(null); setScreenshotSrc(null); }}>
          <option value="">Sélectionner un appareil</option>
          {devices.map(d => (
            <option key={d.device_id || d.id} value={d.device_id || d.id}>
              {d.instagramUsername ? `@${d.instagramUsername}` : ''}{d.device_model ? ` (${d.device_model})` : ''}{!d.instagramUsername && !d.device_model ? (d.device_name || d.device_id || d.id) : ''}
            </option>
          ))}
        </DeviceSelector>
        <ToolsGrid>
          <ToolButton onClick={handleScreenshot} disabled={toolLoading === 'screenshot'}>
            <Camera />{toolLoading === 'screenshot' ? '...' : 'Screenshot'}
          </ToolButton>
          <ToolButton onClick={handleUiDump} disabled={toolLoading === 'ui-dump'}>
            <Code />{toolLoading === 'ui-dump' ? '...' : 'UI Dump'}
          </ToolButton>
          <ToolButton onClick={() => { setScreenshotSrc(null); setUiDumpElements(null); }}>
            <Trash2 />Clear
          </ToolButton>
          <ToolButton onClick={() => handleTap(540, 960)}><MousePointer />Tap Center</ToolButton>
          <ToolButton onClick={() => handleShell()}><Terminal />Shell</ToolButton>
          <StopButton onClick={async () => {
            if (!selectedDevice) return;
            addLog('info', 'Stopping scripts...', 'DEBUG');
            try {
              await fetch('/api/stop-script', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ device_id: selectedDevice }) });
              addLog('success', 'Stop signal sent', 'DEBUG');
            } catch (e) { addLog('error', e.message, 'DEBUG'); }
          }}><Square />Stop</StopButton>
        </ToolsGrid>

        {/* Shell command input */}
        <div style={{ display: 'flex', gap: 4, marginTop: 8 }}>
          <ShellInput
            value={shellCmd}
            onChange={e => setShellCmd(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleShell(); }}
            placeholder="adb shell command..."
            disabled={!selectedDevice}
          />
          <IconBtn onClick={handleShell} title="Run" disabled={!selectedDevice}><Send size={14} /></IconBtn>
        </div>

        {/* Screenshot preview — click to tap */}
        {screenshotSrc && (
          <ScreenshotPreview src={screenshotSrc} alt="Device screenshot" onClick={handleScreenshotClick} title="Click to tap on device" />
        )}

        {/* UI Dump results */}
        {uiDumpElements && (
          <UiDumpResult>
            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--primary)', marginBottom: 4 }}>
              {uiDumpElements.length} UI Elements
            </div>
            {uiDumpElements.slice(0, 60).map((el, i) => (
              <UiElement key={i} onClick={() => handleTap(el.center.x, el.center.y)} title={`Tap (${el.center.x}, ${el.center.y})`}>
                <span style={{ color: 'var(--text-muted)', minWidth: 60 }}>{el.class}</span>
                {el.text && <span style={{ color: '#10b981' }}>"{el.text}"</span>}
                {el.id && <span style={{ color: 'var(--primary)', fontSize: 9 }}>#{el.id}</span>}
                {el.desc && <span style={{ color: '#f59e0b', fontSize: 9 }}>[{el.desc}]</span>}
              </UiElement>
            ))}
          </UiDumpResult>
        )}
      </Section>

      <TabBar>
        <Tab $active={activeTab === 'logs'} onClick={() => setActiveTab('logs')}>
          Logs <TabBadge $hasItems={logs.length > 0}>{logs.length}</TabBadge>
        </Tab>
        <Tab $active={activeTab === 'errors'} onClick={() => setActiveTab('errors')}>
          Errors <TabBadge $hasItems={errorCount + warnCount > 0}>{errorCount + warnCount}</TabBadge>
        </Tab>
        <Tab $active={activeTab === 'status'} onClick={() => setActiveTab('status')}>
          Success <TabBadge $hasItems={false}>{logs.filter(l => l.level === 'success').length}</TabBadge>
        </Tab>
      </TabBar>

      <LogsContainer>
        {filteredLogs.map(log => (
          <LogLine key={log.id} $level={log.level}>
            <LogTimestamp>[{log.ts}]</LogTimestamp>
            <LogTag>[{log.tag}]</LogTag>
            {log.message}
          </LogLine>
        ))}
        <div ref={logsEndRef} />
      </LogsContainer>

      <LogToolbar>
        <FilterBtn $active={filter === 'all'} onClick={() => setFilter('all')}>All</FilterBtn>
        <FilterBtn $active={filter === 'error'} onClick={() => setFilter('error')}>Errors</FilterBtn>
        <FilterBtn $active={filter === 'warn'} onClick={() => setFilter('warn')}>Warn</FilterBtn>
        <FilterBtn $active={filter === 'info'} onClick={() => setFilter('info')}>Info</FilterBtn>
        <div style={{ flex: 1 }} />
        <IconBtn onClick={() => { const next = !paused; setPaused(next); pausedRef.current = next; }} title={paused ? 'Resume' : 'Pause'}>
          {paused ? <Play size={14} /> : <Pause size={14} />}
        </IconBtn>
        <IconBtn onClick={() => navigator.clipboard?.writeText(logs.map(l => `[${l.ts}] ${l.tag} ${l.message}`).join('\n'))} title="Copy">
          <Copy size={14} />
        </IconBtn>
        <IconBtn title="Download"><Download size={14} /></IconBtn>
        <IconBtn onClick={() => setLogs([])} title="Clear"><Trash2 size={14} /></IconBtn>
      </LogToolbar>
    </Panel>
  );
};

export default DebugConsole;
